import { AuthError } from './errors.ts'
import {
  associateSoftwareToken,
  getUser,
  setSoftwareTokenMfaPreference,
  verifySoftwareToken,
} from './idp-operations.ts'
import type { AuthKernel } from './kernel.ts'
import { adminScopedAccessToken } from './scope.ts'

// Enrolling a second factor, as opposed to answering one: sign-in lives in password.ts. Why
// TOTP only, and why nobody can be forced onto it, is in docs/infra.md.

/** The factor Cognito names in `UserMFASettingList`; the pool has no other enabled. */
const SOFTWARE_TOKEN = 'SOFTWARE_TOKEN_MFA'

/** The name the authenticator shows. Here and not per app, so one person sees one entry. */
const ISSUER = 'Volée'

export interface MfaEnrolment {
  /** The base32 secret, for whoever cannot scan. */
  readonly secret: string
  /** The `otpauth://` URI the same secret is scanned from. */
  readonly uri: string
}

export interface MfaApi {
  /** Whether a second factor is already verified on this account. */
  isEnabled(): Promise<boolean>
  /** Issues a secret. Nothing is enabled until `confirm` proves the user can read a code. */
  start(account: string): Promise<MfaEnrolment>
  confirm(code: string): Promise<void>
  disable(): Promise<void>
}

function otpauthUri(secret: string, account: string): string {
  const label = encodeURIComponent(`${ISSUER}:${account}`)
  const params = new URLSearchParams({ secret, issuer: ISSUER })
  return `otpauth://totp/${label}?${params.toString()}`
}

export function createMfaApi(kernel: AuthKernel): MfaApi {
  return {
    async isEnabled() {
      const accessToken = await adminScopedAccessToken(kernel, 'read the second factor of')
      return (await getUser(accessToken)).UserMFASettingList?.includes(SOFTWARE_TOKEN) === true
    },

    async start(account) {
      const accessToken = await adminScopedAccessToken(kernel, 'enrol a second factor on')
      const secret = (await associateSoftwareToken(accessToken)).SecretCode
      if (secret === undefined) {
        throw new AuthError('idp_request_failed', 'The pool issued no secret to enrol')
      }
      return { secret, uri: otpauthUri(secret, account) }
    },

    async confirm(code) {
      const accessToken = await adminScopedAccessToken(kernel, 'enrol a second factor on')
      const status = (await verifySoftwareToken(accessToken, { code, deviceName: ISSUER })).Status
      if (status !== 'SUCCESS') {
        // Cognito answers a refused code with an exception; a non-SUCCESS status is the
        // shape changing under us, and enabling the factor on it would lock the user out.
        throw new AuthError('idp_request_failed', `VerifySoftwareToken answered "${status}"`)
      }
      await setSoftwareTokenMfaPreference(accessToken, true)
    },

    async disable() {
      const accessToken = await adminScopedAccessToken(kernel, 'disable the second factor of')
      await setSoftwareTokenMfaPreference(accessToken, false)
    },
  }
}
